import React from 'react';

import {
	layoutSection,
	layoutSectionWithLocalId,
	layoutSectionWithSingleColumn,
	layoutSectionWithSingleColumnLocalId,
} from '@atlaskit/adf-schema/layout-section';
import { layoutColumn, layoutColumnWithLocalId } from '@atlaskit/adf-schema/nodes/layout-column';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics/types/enums';
import {
	IconFiveColumnLayout,
	IconFourColumnLayout,
	IconLayout,
	IconOneColumnLayout,
	IconThreeColumnLayout,
	IconTwoColumnLayout,
} from '@atlaskit/editor-common/assets';
import {
	TRANSFORM_STRUCTURE_LAYOUT_MENU_ITEM,
	TRANSFORM_STRUCTURE_MENU_SECTION,
} from '@atlaskit/editor-common/block-menu/key';
import { TRANSFORM_STRUCTURE_MENU_SECTION_RANK } from '@atlaskit/editor-common/block-menu/rank';
import { toolbarInsertBlockMessages as messages } from '@atlaskit/editor-common/messages/insert-block';
import { toolbarMessages as layoutMessages } from '@atlaskit/editor-common/messages/layout';
import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory/quick-insert-provider';
import type { FloatingToolbarConfig } from '@atlaskit/editor-common/types/floating-toolbar';
import type { PMPlugin } from '@atlaskit/editor-common/types/plugin-factory';
import type { UiComponentFactoryParams } from '@atlaskit/editor-common/types/ui-components';
import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { TextSelection } from '@atlaskit/editor-prosemirror/state';
import { findParentNode } from '@atlaskit/editor-prosemirror/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/editor-experiment';

import type { LayoutPlugin } from './layoutPluginType';
import {
	createDefaultLayoutSection,
	createMultiColumnLayoutSection,
	deleteLayoutColumn,
	distributeLayoutColumns,
	insertLayoutColumn,
	insertLayoutColumnsWithAnalytics,
	setLayoutColumnDangerPreview,
	setLayoutColumnValign,
	toggleLayoutColumnMenu,
} from './pm-plugins/actions';
import { default as createLayoutKeymapPlugin } from './pm-plugins/keymap';
import { default as createLayoutPlugin } from './pm-plugins/main';
import { pluginKey } from './pm-plugins/plugin-key';
import { default as createLayoutResizingPlugin } from './pm-plugins/resizing';
import type { LayoutState } from './pm-plugins/types';
import { GlobalStylesWrapper } from './ui/global-styles';
import { createLayoutBlockMenuItem } from './ui/LayoutBlockMenuItem';
import { LayoutColumnMenu } from './ui/LayoutColumnMenu';
import { getLayoutColumnMenuComponents } from './ui/LayoutColumnMenu/components';
import { getLayoutQuickInsertComponents } from './ui/quick-insert/getLayoutQuickInsertComponents';
import { buildToolbar } from './ui/toolbar';

const LAYOUT_SECTION_NODE_NAME = 'layoutSection';

/**
 * This function is used to set the selection into
 * the first paragraph of the first column of a layout section.
 * This function is only intended to be used after inserting a new layout section.
 * @param tr - transaction
 * @returns - transaction with the selection set to the first paragraph of the first column
 */
export const selectIntoLayoutSection = (tr: Transaction): Transaction => {
	if (!editorExperiment('single_column_layouts', true)) {
		return tr;
	}

	const { layoutSection, paragraph } = tr.doc.type.schema.nodes;
	const nodeWithPos = findParentNode((node) => node.type === layoutSection)(tr.selection);

	if (
		!nodeWithPos ||
		!nodeWithPos.node ||
		nodeWithPos.node.type.name !== 'layoutSection' ||
		nodeWithPos.node.firstChild?.firstChild?.type !== paragraph
	) {
		return tr;
	}

	// set text selection at the beginning of the layout section
	// will set the selection to the first column
	tr.setSelection(TextSelection.create(tr.doc, nodeWithPos.pos));
	return tr;
};

export const layoutPlugin: LayoutPlugin = ({ config: options = {}, api }) => {
	const isAdvancedLayoutEnabled = editorExperiment('advanced_layouts', true);
	const allowAdvancedSingleColumnLayout =
		isAdvancedLayoutEnabled && editorExperiment('single_column_layouts', true, { exposure: true });
	const isRegisteredSlashCommandEnabled =
		isExperimentEnabled('platform_editor_slash_command') && isAdvancedLayoutEnabled;

	api?.blockMenu?.actions.registerBlockMenuComponents([
		{
			type: 'block-menu-item',
			key: TRANSFORM_STRUCTURE_LAYOUT_MENU_ITEM.key,
			parent: {
				type: 'block-menu-section' as const,
				key: TRANSFORM_STRUCTURE_MENU_SECTION.key,
				rank: (TRANSFORM_STRUCTURE_MENU_SECTION_RANK as Record<string, number>)[
					TRANSFORM_STRUCTURE_LAYOUT_MENU_ITEM.key
				],
			},
			component: createLayoutBlockMenuItem(api),
			isHidden: () =>
				Boolean(api?.blockMenu?.actions.isTransformOptionDisabled(LAYOUT_SECTION_NODE_NAME)),
		},
	]);

	api?.uiControlRegistry?.actions.register(getLayoutColumnMenuComponents({ api }));

	if (isRegisteredSlashCommandEnabled) {
		api?.uiControlRegistry?.actions.register(getLayoutQuickInsertComponents({ api }));
	}

	return {
		name: 'layout',

		nodes() {
			return [
				{
					name: 'layoutSection',
					node: editorExperiment('advanced_layouts', true)
						? fg('platform_editor_adf_with_localid')
							? layoutSectionWithSingleColumnLocalId
							: layoutSectionWithSingleColumn
						: fg('platform_editor_adf_with_localid')
							? layoutSectionWithLocalId
							: layoutSection,
				},
				{
					name: 'layoutColumn',
					node: fg('platform_editor_adf_with_localid') ? layoutColumnWithLocalId : layoutColumn,
				},
			];
		},

		pmPlugins() {
			const isViewMode = () => api?.editorViewMode?.sharedState.currentState()?.mode === 'view';
			const plugins = [
				{
					name: 'layout',
					plugin: () => createLayoutPlugin(options, api?.analytics?.actions, isViewMode),
				},
			] as Array<PMPlugin>;

			plugins.push({
				name: 'layoutKeymap',
				plugin: () => createLayoutKeymapPlugin({ api }),
			});

			if (
				(options.editorAppearance === 'full-page' ||
					options.editorAppearance === 'full-width' ||
					options.editorAppearance === 'max') &&
				api &&
				editorExperiment('advanced_layouts', true)
			) {
				plugins.push({
					name: 'layoutResizing',
					plugin: ({ portalProviderAPI, eventDispatcher, getIntl }) =>
						createLayoutResizingPlugin(options, api, portalProviderAPI, eventDispatcher, getIntl()),
				});
			}
			return plugins;
		},

		actions: {
			insertLayoutColumns: insertLayoutColumnsWithAnalytics(api?.analytics?.actions),
		},

		pluginsOptions: {
			floatingToolbar(state, intl): FloatingToolbarConfig | undefined {
				const layoutState = pluginKey.getState(state) as LayoutState | undefined;
				if (!layoutState) {
					return undefined;
				}
				const { pos, allowBreakout, addSidebarLayouts, allowSingleColumnLayout, isResizing } =
					layoutState;

				const shouldHideToolbar = isResizing && editorExperiment('single_column_layouts', true);

				if (pos !== null && !shouldHideToolbar) {
					return buildToolbar(
						state,
						intl,
						pos,
						allowBreakout,
						addSidebarLayouts,
						allowSingleColumnLayout,
						allowAdvancedSingleColumnLayout,
						api,
					);
				}
				return undefined;
			},
			quickInsert: isRegisteredSlashCommandEnabled
				? undefined
				: ({ formatMessage }) => {
						const withInsertLayoutAnalytics = (tr: Transaction, columnCount: number) => {
							api?.analytics?.actions?.attachAnalyticsEvent({
								action: ACTION.INSERTED,
								actionSubject: ACTION_SUBJECT.DOCUMENT,
								actionSubjectId: ACTION_SUBJECT_ID.LAYOUT,
								attributes: {
									inputMethod: INPUT_METHOD.QUICK_INSERT,
									columnCount,
								},
								eventType: EVENT_TYPE.TRACK,
							})(tr);

							return tr;
						};

						if (editorExperiment('advanced_layouts', true)) {
							const createAdvancedColumnLayoutOption = ({
								columnCount,
								descriptionColumnCount,
								icon: Icon,
								id,
								keyword,
								title,
							}: {
								columnCount: number;
								descriptionColumnCount: string;
								icon: QuickInsertItem['icon'];
								id: QuickInsertItem['id'];
								keyword: string;
								title: string;
							}): QuickInsertItem => ({
								id,
								title,
								description: formatMessage(messages.columnsDescriptionAdvancedLayout, {
									numberOfColumns: descriptionColumnCount,
								}),
								keywords: ['layout', 'column', 'section', 'col', keyword],
								priority: 1100,
								icon: Icon,
								action(insert, state) {
									const tr = insert(createMultiColumnLayoutSection(state, columnCount));
									withInsertLayoutAnalytics(tr, columnCount);
									selectIntoLayoutSection(tr);
									return tr;
								},
							});

							const advancedSingleColumnOption: QuickInsertItem[] = allowAdvancedSingleColumnLayout
								? [
										{
											id: 'onecolumnlayout',
											title: formatMessage(layoutMessages.singleColumnAdvancedLayout),
											description: formatMessage(messages.singleColumnsDescriptionAdvancedLayout),
											keywords: ['layout', 'column', 'section', 'col', 'single column'],
											priority: 1100,
											icon: () => <IconOneColumnLayout />,
											action(insert, state) {
												const tr = insert(createMultiColumnLayoutSection(state, 1));
												withInsertLayoutAnalytics(tr, 1);

												selectIntoLayoutSection(tr);
												return tr;
											},
										},
									]
								: [];

							return [
								createAdvancedColumnLayoutOption({
									columnCount: 2,
									descriptionColumnCount: 'two',
									icon: () => <IconTwoColumnLayout />,
									id: 'twocolumnslayout',
									keyword: 'two column',
									title: formatMessage(layoutMessages.twoColumnsAdvancedLayout),
								}),
								...advancedSingleColumnOption,
								createAdvancedColumnLayoutOption({
									columnCount: 3,
									descriptionColumnCount: 'three',
									icon: () => <IconThreeColumnLayout />,
									id: 'threecolumnslayout',
									keyword: 'three column',
									title: formatMessage(layoutMessages.threeColumnsAdvancedLayout),
								}),
								createAdvancedColumnLayoutOption({
									columnCount: 4,
									descriptionColumnCount: 'four',
									icon: () => <IconFourColumnLayout />,
									id: 'fourcolumnslayout',
									keyword: 'four column',
									title: formatMessage(layoutMessages.fourColumns),
								}),
								createAdvancedColumnLayoutOption({
									columnCount: 5,
									descriptionColumnCount: 'five',
									icon: () => <IconFiveColumnLayout />,
									id: 'fivecolumnslayout',
									keyword: 'five column',
									title: formatMessage(layoutMessages.fiveColumns),
								}),
							];
						} else {
							return [
								{
									id: 'layout',
									title: formatMessage(messages.columns),
									description: formatMessage(messages.columnsDescription),
									keywords: ['column', 'section'],
									priority: 1100,
									icon: () => <IconLayout />,
									action(insert, state) {
										const tr = insert(createDefaultLayoutSection(state));
										api?.analytics?.actions?.attachAnalyticsEvent({
											action: ACTION.INSERTED,
											actionSubject: ACTION_SUBJECT.DOCUMENT,
											actionSubjectId: ACTION_SUBJECT_ID.LAYOUT,
											attributes: {
												inputMethod: INPUT_METHOD.QUICK_INSERT,
											},
											eventType: EVENT_TYPE.TRACK,
										})(tr);
										return tr;
									},
								},
							];
						}
					},
		},
		contentComponent({
			editorView,
			popupsMountPoint,
			popupsBoundariesElement,
			popupsScrollableElement,
		}: UiComponentFactoryParams) {
			return (
				<>
					{editorExperiment('advanced_layouts', true) ? <GlobalStylesWrapper /> : null}
					{editorView ? (
						<LayoutColumnMenu
							api={api}
							editorView={editorView}
							mountTo={popupsMountPoint}
							boundariesElement={popupsBoundariesElement}
							scrollableElement={popupsScrollableElement}
						/>
					) : null}
				</>
			);
		},
		getSharedState(editorState) {
			if (!editorState) {
				return undefined;
			}
			return pluginKey.getState(editorState);
		},
		commands: {
			deleteLayoutColumn: (options) => deleteLayoutColumn(options, api?.analytics?.actions, api),
			distributeLayoutColumns: (options) =>
				distributeLayoutColumns(api?.analytics?.actions, api)(options),
			insertLayoutColumn: (options) => insertLayoutColumn(options, api?.analytics?.actions, api),
			setLayoutColumnDangerPreview,
			setLayoutColumnValign: (options) =>
				setLayoutColumnValign(options, api?.analytics?.actions, api),
			toggleLayoutColumnMenu,
		},
	};
};
